import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Overline } from "./Motifs";
import { BOOKSHELF } from "../data/content";

const TABS = [
  { id: "children", label: "Children's Books" },
  { id: "poetry", label: "Poetry" },
  { id: "anthologies", label: "Anthologies & Stories" },
];

export default function Bookshelf() {
  const [active, setActive] = useState("children");
  const list = BOOKSHELF[active];

  return (
    <section
      id="bookshelf"
      data-testid="bookshelf-section"
      className="relative py-24 sm:py-32 bg-[#1E2A4F] text-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline className="!text-[#E76F51]">The Library</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] tracking-tight">
              Books, Poetry &<br />
              <em className="italic font-light">other small worlds.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#F9F6F0]/70 leading-relaxed">
            A shelf gathered over many years — picture books for the very small, poems for the
            quietly heartbroken, and short stories that wandered into anthologies.
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-14 flex flex-wrap gap-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`bookshelf-tab-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-all duration-500 ${
                active === t.id
                  ? "bg-[#D95D39] text-[#F9F6F0]"
                  : "border border-[#F9F6F0]/30 text-[#F9F6F0]/80 hover:border-[#F9F6F0]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Bookshelf */}
        <div className="mt-14 border-t border-[#F9F6F0]/15">
          <AnimatePresence mode="wait">
            <motion.ul
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="divide-y divide-[#F9F6F0]/15"
            >
              {list.map((b, i) => {
                const Wrapper = b.purchase ? "a" : "li";
                const wrapperProps = b.purchase
                  ? {
                      href: b.purchase,
                      target: "_blank",
                      rel: "noreferrer",
                    }
                  : {};
                return (
                  <li
                    key={b.title}
                    data-testid={`bookshelf-item-${active}-${i}`}
                    className="group transition-colors duration-300 -mx-2 rounded"
                  >
                    <Wrapper
                      {...wrapperProps}
                      data-testid={b.purchase ? `bookshelf-purchase-${active}-${i}` : undefined}
                      className="grid grid-cols-12 gap-4 sm:gap-8 py-6 sm:py-8 items-center hover:bg-[#F9F6F0]/5 transition-colors duration-300 px-2 rounded"
                    >
                      <span className="col-span-2 sm:col-span-1 font-display text-[#E76F51] text-lg tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {b.cover ? (
                        <div className="col-span-10 sm:col-span-2 aspect-square overflow-hidden rounded-md border border-[#F9F6F0]/15">
                          <img
                            src={b.cover}
                            alt={b.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                      ) : (
                        <div className="hidden sm:block sm:col-span-2" />
                      )}
                      <h3 className={`${b.cover ? "col-span-12 sm:col-span-4" : "col-span-10 sm:col-span-6"} font-display text-2xl sm:text-3xl lg:text-4xl leading-tight group-hover:text-[#E76F51] transition-colors duration-500`}>
                        {b.title}
                      </h3>
                      <p className="col-span-12 sm:col-span-4 text-sm text-[#F9F6F0]/60 leading-relaxed">
                        {b.subtitle}
                      </p>
                      {b.purchase && (
                        <span className="col-span-12 sm:col-span-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#E76F51] sm:justify-self-end opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          Find on Amazon →
                        </span>
                      )}
                    </Wrapper>
                  </li>
                );
              })}
            </motion.ul>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
